import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { Button } from '@/components/atoms/button'
import { Hint } from '@/components/atoms/hint'
import { InputField } from '@/components/molecules/input-field'
import { Label } from '@/components/molecules/label'
import { RadioGroupField } from '@/components/molecules/radio-group-field'
import {
  modalTitleStyle,
  modalItemWrapperStyle,
  modalFooterStyle,
} from '@/components/pages/setting/guest-room/room/_room/modal/modal-layout'
import {
  roomRateModalOverlayStyle,
  roomRateModalPanelPlacementStyle,
  roomRateModalSectionStyle,
} from '@/components/pages/setting/room-rate/shared-styles'
import {
  isPriceRankColorKey,
  PRICE_RANK_COLOR_ORDER,
  PRICE_RANK_COLORS,
  resolvePriceRankColorKeyFromApi,
  toApiPriceRankColor,
} from '@/models/room-rate/price-rank'
import type {
  PriceRank,
  PriceRankCalendarDefaultType,
  PriceRankColorKey,
  PriceRankFormValues,
  PriceRankInput,
} from '@/models/room-rate/price-rank'

type Props = {
  title: string
  editRank?: PriceRank
  /** 他ランクが使用中の初期値区分。編集対象自身は含めない */
  usedCalendarDefaultTypes: PriceRankCalendarDefaultType[]
  nextDisplayOrder: number
  onClickCancel: () => void
  onClickSave: (values: PriceRankInput) => void | Promise<void>
}

export const PriceRankModal: React.FC<Props> = ({
  title,
  editRank,
  usedCalendarDefaultTypes,
  nextDisplayOrder,
  onClickCancel,
  onClickSave,
}) => {
  const { t } = useTranslation()
  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { isValid, errors },
  } = useForm<PriceRankFormValues>({
    mode: 'onChange',
    defaultValues: {
      // displayOrder は 0 があり得るため || ではなく ??
      displayOrder: String(editRank?.displayOrder ?? nextDisplayOrder),
      shortName: editRank?.shortName || '',
      name: editRank?.name || '',
      calendarDefaultType: editRank?.calendarDefaultType || 'none',
      // GET は hex。UI 選択は key。未知 hex は disabled 表示用に raw を残す
      color: (editRank?.color && resolvePriceRankColorKeyFromApi(editRank.color)) || editRank?.color || 'black',
    },
  })

  const watchColor = watch('color')
  const resolvedEditColorKey = editRank?.color ? resolvePriceRankColorKeyFromApi(editRank.color) : undefined
  const unknownColor = editRank?.color && !resolvedEditColorKey && !isPriceRankColorKey(editRank.color) ? editRank.color : undefined
  const usedCalendarDefaultTypeSet = new Set(usedCalendarDefaultTypes)

  const submit = (values: PriceRankFormValues) => {
    onClickSave({
      ...values,
      displayOrder: Number(values.displayOrder),
      color: toApiPriceRankColor(values.color),
    })
  }

  const isCalendarDefaultDisabled = (value: PriceRankCalendarDefaultType) => {
    if (value === 'none') return false
    // 編集時は自身の現在値は選択可のまま
    if (editRank?.calendarDefaultType === value) return false
    return usedCalendarDefaultTypeSet.has(value)
  }

  const calendarDefaultItems: { value: PriceRankCalendarDefaultType; label: string; disabled?: boolean }[] = [
    { value: 'none', label: t('priceRank.calendarDefault.none') },
    {
      value: 'weekday',
      label: t('priceRank.calendarDefault.weekday'),
      disabled: isCalendarDefaultDisabled('weekday'),
    },
    {
      value: 'holiday',
      label: t('priceRank.calendarDefault.holiday'),
      disabled: isCalendarDefaultDisabled('holiday'),
    },
    {
      value: 'pre_holiday',
      label: t('priceRank.calendarDefault.dayBeforeHoliday'),
      disabled: isCalendarDefaultDisabled('pre_holiday'),
    },
  ]

  return (
    <div css={roomRateModalOverlayStyle}>
      <div css={[modalPanelStyle, roomRateModalPanelPlacementStyle]}>
        <div css={modalTitleStyle}>{title}</div>
        <div css={modalSectionStyle}>
          <div css={modalItemWrapperStyle}>
            <Controller
              name="displayOrder"
              control={control}
              rules={{
                required: t('Required field has not been entered'),
                pattern: { value: /^[0-9]+$/, message: t('Please enter half-width numbers') },
              }}
              render={({ field: { onChange, value, name } }) => (
                <InputField
                  type="tel"
                  value={value}
                  name={name}
                  handleChangeData={onChange}
                  label={t('Display order')}
                  placeholder="00000"
                  error={errors.displayOrder?.message}
                  required
                />
              )}
            />

            <Controller
              name="shortName"
              control={control}
              rules={{ required: true, maxLength: 3 }}
              render={({ field: { onChange, value, name } }) => (
                <InputField
                  value={value}
                  name={name}
                  handleChangeData={onChange}
                  label={t('Name (abbreviation)')}
                  placeholder={t('priceRank.shortNamePlaceholder')}
                  maxLength={3}
                  required
                />
              )}
            />

            <Controller
              name="name"
              control={control}
              rules={{ required: true, maxLength: 255 }}
              render={({ field: { onChange, value, name } }) => (
                <div css={nameFieldStyle}>
                  <Label label={t('Rate rank name')} required />
                  <textarea
                    name={name}
                    value={value}
                    placeholder={t('priceRank.namePlaceholder')}
                    css={textareaStyle}
                    maxLength={255}
                    onChange={e => onChange(e.target.value)}
                  />
                </div>
              )}
            />

            <div css={fieldBlockStyle}>
              <div css={labelWithHintStyle}>
                <Label label={t('Rate calendar default')} required />
                <Hint customCss={helpHintStyle}>
                  <div css={helpPopupContentStyle}>{t('priceRank.calendarDefaultHint')}</div>
                </Hint>
              </div>
              <Controller
                name="calendarDefaultType"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange, value } }) => (
                  <RadioGroupField<PriceRankCalendarDefaultType>
                    value={value}
                    name="calendarDefaultType"
                    items={calendarDefaultItems}
                    onChange={e => onChange(e.target.value as PriceRankCalendarDefaultType)}
                    style={{ radioSize: 21, labelMargin: 12, direction: 'row' }}
                    itemCss={radioItemStyle}
                    itemWrapperCss={radioItemWrapperStyle}
                    groupCss={radioGroupStyle}
                  />
                )}
              />
            </div>

            <div css={colorFieldStyle}>
              <Label label={t('Color')} required />
              <Controller
                name="color"
                control={control}
                rules={{ required: true }}
                render={({ field: { onChange } }) => (
                  <ul css={colorListStyle}>
                    {unknownColor && (
                      // マスタ不整合の既存色は表示のみ残し、選択し直させない
                      <li css={[colorItemStyle, colorItemDisabledStyle]}>
                        <div
                          css={[
                            colorSwatchStyle,
                            {
                              background: unknownColor,
                              opacity: 0.7,
                            },
                          ]}
                        >
                          {watchColor === unknownColor && (
                            <img src={require('@/static/images/room-rate/color-check.svg')} alt="" css={colorCheckImgStyle} />
                          )}
                        </div>
                        <p css={unknownColorTextStyle}>{unknownColor}</p>
                      </li>
                    )}
                    {PRICE_RANK_COLOR_ORDER.map(key => {
                      const colorDef = PRICE_RANK_COLORS[key]
                      const selected = watchColor === key
                      return (
                        <li
                          key={key}
                          css={colorItemStyle}
                          onClick={() => {
                            onChange(key)
                            setValue('color', key as PriceRankColorKey, { shouldValidate: true })
                          }}
                        >
                          <div css={[colorSwatchStyle, { background: colorDef.color }]}>
                            {selected && <img src={require('@/static/images/room-rate/color-check.svg')} alt="" css={colorCheckImgStyle} />}
                          </div>
                          <p style={{ color: colorDef.color }}>{t(colorDef.labelKey)}</p>
                        </li>
                      )
                    })}
                  </ul>
                )}
              />
            </div>
          </div>
        </div>
        <div css={[modalFooterStyle, footerOverrideStyle]}>
          <Button buttonType={3} width={110} height={38} fontSize={14} marginRight={16} onClick={onClickCancel}>
            {t('Cancel')}
          </Button>
          <Button
            buttonType={!isValid ? 5 : 1}
            width={110}
            height={38}
            fontSize={14}
            marginRight={32}
            onClick={handleSubmit(submit)}
            isDisabled={!isValid}
          >
            {t('Save')}
          </Button>
        </div>
      </div>
    </div>
  )
}

const modalPanelStyle = css({
  width: 600,
  maxHeight: 'calc(100vh - 64px)',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 5,
  overflow: 'hidden',
  boxShadow: '0px 0px 6px rgba(0, 0, 0, 0.1)',
  background: '#fff',
})

const modalSectionStyle = css(roomRateModalSectionStyle, {
  overflowY: 'auto',
  // タイトル(60) + フッター(75) + 上下マージン(64) を除いた本文高さ
  maxHeight: 'calc(100vh - 199px)',
})

const fieldBlockStyle = css({
  marginBottom: 0,
})

const nameFieldStyle = css({
  marginBottom: 24,
})

const textareaStyle = css({
  width: '100%',
  minHeight: 75,
  padding: '8px 15px',
  border: '1px solid #CCCCCC',
  borderRadius: 10,
  fontSize: 14,
  lineHeight: '21px',
  color: '#272727',
  resize: 'vertical',
  '::placeholder': {
    color: '#CCCCCC',
  },
})

const labelWithHintStyle = css({
  display: 'flex',
  alignItems: 'center',
  '.__label': {
    marginBottom: 0,
  },
})

const helpHintStyle = css({
  marginLeft: 8,
  marginBottom: 11,
  '.help-icon': {
    width: 18,
    height: 18,
  },
  '.help-popup': {
    left: 24,
    top: -8,
    width: 280,
    padding: 12,
  },
})

const helpPopupContentStyle = css({
  fontSize: 12,
  lineHeight: '18px',
  color: '#272727',
  fontWeight: 'normal',
})

const radioGroupStyle = css({
  gap: 24,
  alignItems: 'center',
  overflow: 'visible',
})

const radioItemWrapperStyle = css({
  float: 'none',
})

const radioItemStyle = css({
  float: 'none',
})

const colorFieldStyle = css({
  // 料金カレンダー初期値とのあいだ（他フィールド間隔に合わせ24px）
  marginTop: 24,
})

const colorListStyle = css({
  display: 'flex',
  flexWrap: 'wrap',
  gap: '16px 16px',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  width: 405,
})

const colorItemStyle = css({
  width: 65,
  cursor: 'pointer',
  textAlign: 'center',
  p: {
    marginTop: 8,
    fontSize: 12,
    fontWeight: 'bold',
    lineHeight: 1.2,
  },
})

const colorItemDisabledStyle = css({
  cursor: 'default',
  pointerEvents: 'none',
})

const unknownColorTextStyle = css({
  color: '#676767',
})

const colorSwatchStyle = css({
  width: 65,
  height: 65,
  borderRadius: '50%',
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
})

const colorCheckImgStyle = css({
  width: 27,
  height: 20,
  display: 'block',
})

const footerOverrideStyle = css({
  height: 75,
})
